/**
 * Neko Kuji Thumbnail Switcher
 * Custom element for switching product images via thumbnail clicks
 * 玩具喵 Neko Toys - Ichiban Kuji Product Card
 */

if (!customElements.get('neko-kuji-thumbnail-switcher')) {
  customElements.define(
    'neko-kuji-thumbnail-switcher',
    class NekoKujiThumbnailSwitcher extends HTMLElement {
      constructor() {
        super();

        // Cache DOM references
        this.elements = {
          mainImage: this.querySelector('[data-main-image]'),
          thumbnails: this.querySelector('[data-thumbnails]'),
          thumbnailButtons: this.querySelectorAll('[data-media-id]')
        };

        // Guard: exit if no thumbnails (single image product)
        if (!this.elements.thumbnails || this.elements.thumbnailButtons.length === 0) {
          return;
        }

        // Initialize event listeners
        this.initEventListeners();
      }

      /**
       * Initialize all event listeners
       */
      initEventListeners() {
        this.elements.thumbnailButtons.forEach((button) => {
          // Click event
          button.addEventListener('click', this.handleThumbnailClick.bind(this));

          // Keyboard events (Enter/Space)
          button.addEventListener('keydown', this.handleThumbnailKeydown.bind(this));
        });
      }

      /**
       * Handle thumbnail click
       * @param {Event} event - Click event
       */
      handleThumbnailClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const mediaId = button.dataset.mediaId;

        this.switchImage(button, mediaId);
      }

      /**
       * Handle keyboard navigation
       * @param {KeyboardEvent} event - Keydown event
       */
      handleThumbnailKeydown(event) {
        // Only handle Enter and Space
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();
        const button = event.currentTarget;
        const mediaId = button.dataset.mediaId;

        this.switchImage(button, mediaId);
      }

      /**
       * Switch main image and update active state
       * @param {HTMLElement} button - Clicked thumbnail button
       * @param {string} mediaId - Media ID to switch to
       */
      switchImage(button, mediaId) {
        // Get the thumbnail image data
        const thumbnailImg = button.querySelector('img');
        if (!thumbnailImg) return;

        // Extract base URL from thumbnail (remove query params and size suffix)
        const thumbnailSrc = thumbnailImg.src;
        const baseSrc = this.getBaseSrc(thumbnailSrc);

        // Build new srcset for main image (high res versions)
        const newSrcset = this.buildMainImageSrcset(baseSrc);
        const newSrc = this.getImageUrl(baseSrc, 900);

        // Update main image
        if (this.elements.mainImage) {
          this.elements.mainImage.src = newSrc;
          this.elements.mainImage.srcset = newSrcset;
          this.elements.mainImage.alt = thumbnailImg.alt;
        }

        // Update active state
        this.updateActiveThumbnail(button);
      }

      /**
       * Extract base source URL from Shopify CDN URL
       * @param {string} src - Full image URL
       * @returns {string} Base URL without size suffix
       */
      getBaseSrc(src) {
        // Remove query parameters
        const urlWithoutQuery = src.split('?')[0];

        // Remove Shopify size suffix (e.g., _120x.jpg -> .jpg)
        return urlWithoutQuery.replace(/_\d+x(\.\w+)$/, '$1');
      }

      /**
       * Build srcset for main image with larger sizes
       * @param {string} baseSrc - Base image source URL
       * @returns {string} Srcset string
       */
      buildMainImageSrcset(baseSrc) {
        const sizes = [360, 533, 720, 900];
        const srcsetParts = sizes.map(width => {
          return `${this.getImageUrl(baseSrc, width)} ${width}w`;
        });
        return srcsetParts.join(', ');
      }

      /**
       * Get Shopify CDN image URL with specific width
       * @param {string} src - Original image URL
       * @param {number} width - Desired width
       * @returns {string} Resized image URL
       */
      getImageUrl(src, width) {
        // Find file extension
        const match = src.match(/(\.\w+)$/);
        if (!match) return src;

        const extension = match[1];

        // Insert width parameter before file extension
        // Example: image.jpg -> image_900x.jpg
        return src.replace(/(\.\w+)$/, `_${width}x${extension}`);
      }

      /**
       * Update aria-current on thumbnails
       * @param {HTMLElement} activeButton - The newly active thumbnail button
       */
      updateActiveThumbnail(activeButton) {
        // Remove aria-current from all thumbnails
        this.elements.thumbnailButtons.forEach((btn) => {
          btn.removeAttribute('aria-current');
        });

        // Set aria-current on active thumbnail
        activeButton.setAttribute('aria-current', 'true');
      }
    }
  );
}
